import { cloneDeep } from "lodash";
import { HttpClient, HttpEvent, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, finalize, map, Observable, of, switchMap, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { IPayload, IQuestion, Payload } from "../interface/filters";
import {
  genInterviewSummaryTableConfig
} from "../modules/pages/interview-summary/interview-summary/interview-summary-table-config";
import { InterviewAdapter } from "../modules/pages/interview-summary/models/interview-adapter";
import { InterviewSummary } from "../modules/pages/interview-summary/models/interview-summary";
import { IApiService } from "../modules/records/models/api/i-api-service";
import { IMappedApiCollection } from "../modules/records/models/api/i-mapped-api-collection";
import { TextCol } from "../modules/records/models/columns/text-col";
import { TableStructure } from "../modules/records/models/table-structure";
import { RatingCol } from "../shared/table-col-configs/rating-col";
import { ApplicationService } from "./application.service";
import { MyUtils } from "./my-utils.service";
import { RecoveryPlanService } from "./recovery-plan.service";
import { AuthService } from "./auth.service";
import { MeUser } from "./models/me-user";
import { Router } from "@angular/router";
import { Role } from "../enums/role";
import { DownloadLogService } from "./download-log.service";
import { TagOnlineRepDialogComponent } from "../shared/dialogs/tag-online-rep-dialog/tag-online-rep-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { OnlineRepCommentDialogComponent } from "../shared/dialogs/online-rep-comment-dialog/online-rep-comment-dialog.component";
import { DataMiningService } from "./data-mining.service";
import { QuestionsService } from "./questions.service";
import { LoaderService } from "./loader.service";

@Injectable({
  providedIn: "root",
})
export class InterviewsService implements IApiService<InterviewSummary> {
  lastSort: string | null = null;
  lastPageIndexQuery: number = 0;
  isShopper: boolean = false;
  endpoint = environment.csibefrontend;
  isLoading = false;
  isInterviewSync = true
  user: MeUser = {
    agent: null,
    csiLevel: Role.ROLE_DEALER,
    brand: "",
    csiProfile: Role.ROLE_DEALER,
    dealerInfo: [],
    dealer: "",
    dealerGroup: [],
    dealerName: "",
    defaultLocale: "",
    distributor: null,
    elementBrandColor: "",
    email: "",
    firstBrandColor: "",
    market: "",
    menu: [],
    outlet: "",
    pathbrand: "",
    profileCodeMenu: "",
    region: "",
    roles: [],
    scope: "",
    scopeEntities: { network: "", vehicle: "" },
    secondBrandColor: "",
    subRegion: ""
  };
  question: IQuestion = {
    questionCode: "",
    section: "",
    tab: "",
    orderByScore: "",
    locale: "",
    limit: ""
  }
  totalRecords: number = 0;
  public tableDatasSubject: BehaviorSubject<any>;
  public tableDatas: Observable<any>;
  private _interviews = new BehaviorSubject<InterviewSummary[]>([]);

  private idsTableSubject: BehaviorSubject<any>;
  public idsTable: Observable<string[]>;

  public interviewSummaryPath?: string;
  public exportInterviewSummaryPath?: string;

  get item$() {
    return this._interviews.asObservable();
  }
  public setIdsTableValue(idsTable: any) {
    localStorage.setItem("idsTable", JSON.stringify(idsTable));
    this.idsTableSubject.next(<any>idsTable);
  }
  public get idTableValue() {
    return this.idsTableSubject.value;
  }

  constructor(
    private interviewAdapter: InterviewAdapter,
    private http: HttpClient,
    private utils: MyUtils,
    private recoveryPlanService: RecoveryPlanService,
    private applicationService: ApplicationService,
    private dataMiningService: DataMiningService,
    private ds: DownloadLogService,
    public router: Router,
    private questionsService: QuestionsService,
    private dialog: MatDialog,
    private loader: LoaderService
  ) {
    this.tableDatasSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("tableDatas")!)
    );
    this.tableDatas = this.tableDatasSubject.asObservable();

    this.idsTableSubject = new BehaviorSubject<any>([]);
    this.idsTable = this.idsTableSubject.asObservable();
    if (this.applicationService.navBarSelectValue?.displayName.includes("CEC") || this.applicationService.navBarSelectValue?.displayName === "B2E") {
      this.interviewSummaryPath = "v3/getInterviewsSummary";
      this.exportInterviewSummaryPath = "v3/exportInterviewsSummary"
    } else {
      this.interviewSummaryPath = "v4/getInterviewsSummary";
      this.exportInterviewSummaryPath = "v4/exportInterviewsSummary"
    }
    // if (this.router?.url === "/interviewsSummary/interview-summary-elastic") {
    //   //console.log(this.router?.url)
    //   this.interviewSummaryPath = "v4/getInterviewsSummary"
    //   this.exportInterviewSummaryPath = "v4/exportInterviewsSummary"
    // } else if(this.applicationService.menuIteSelectValue === 'Interviews summary') {
    //   //console.log(this.applicationService.menuIteSelectValue)
    //   this.interviewSummaryPath = "v3/getInterviewsSummary"
    //   this.exportInterviewSummaryPath = "v3/exportInterviewsSummary"
    // }
  }


  upload(file: File, additionalData: Map<string, string> | undefined): Observable<HttpEvent<any>> {
    const formData: FormData = new FormData();

    formData.append('file', file);

    if (additionalData) {
      for (let [key, value] of additionalData.entries()) {
        if (value != null)
          formData.append(key, value);
      }
    }
    const req = new HttpRequest('POST', `${this.endpoint}interviews/v1/recall-campaign/upload`, formData, {
      reportProgress: true,
      responseType: 'json'
    });

    return this.http.request(req);
  }
  public setTableDatasValue(idsTable: any) {
    localStorage.setItem("tableDatas", JSON.stringify(idsTable));
    this.tableDatasSubject.next(<any>idsTable);
  }

  public get tableDatasValue() {
    return this.tableDatasSubject.value;
  }

  getAll(
    filter: IPayload,
    skip: number,
    limit: number,
    sortDirection: string,
    sortBy: string,
    extraFilters: {
      NPS: string;
      NSAT: string;
      channel: string;
      searchBy: string;
      searchFor: string;
      recovery: string;
    },
    tableConfig: TableStructure<InterviewSummary>
  ) {
    this.totalRecords = 0;
    // if (this.router?.url === "/interviewsSummary/interview-summary-elastic") {
    //   //console.log(this.router?.url)
    //   this.interviewSummaryPath = "v4/getInterviewsSummary"
    //   this.exportInterviewSummaryPath = "v4/exportInterviewsSummary"
    // }
    // else if(this.applicationService.menuIteSelectValue === "/interviewsSummary/interview-summary") {
    //   //console.log(this.applicationService.menuIteSelectValue)
    //   this.interviewSummaryPath = "v3/getInterviewsSummary"
    //   this.exportInterviewSummaryPath = "v3/exportInterviewsSummary"
    // }




    let payload = this.buildFilters(filter, skip, limit, sortDirection, sortBy, extraFilters);
    this.isLoading = true;
    return this.http.post<any>(this.endpoint + this.interviewSummaryPath, payload).pipe(
      catchError((err) => {
        this.isLoading = false;
        return of(null);
      }),
      map((res) => {
        if (this.applicationService.navBarSelectValue.idItem === "ONLINEREP") {
          if (res) {
            if (res.paginationInfo && Array.isArray(res.paginationInfo)) {
              res.paginationInfo = res.paginationInfo[0];
              this.lastSort = res.paginationInfo.lastSort
            }
          }
          return this.interviewAdapter.mapCollection(res);

        } else {

          if (res) {
            if (res.paginationInfo && Array.isArray(res.paginationInfo)) {
              res.paginationInfo = res.paginationInfo[0];
              this.lastSort = res.paginationInfo.lastSort
            }
            else {
              res.data = [];
              res.paginationInfo = [{
                itemsPerPage: payload.extraFilters.ItemsPerPage,
                page: payload.extraFilters.Page,
                totalElements: 0,
                totalPages: 0
              }];
              //this.lastSort = res.paginationInfo.lastSort
            }
          }
          return this.interviewAdapter.mapCollection(res);
        }


      }),
      tap((res) => {
        this.totalRecords = res.count;
        this.setTableDatasValue(res.items);
        //if (this.router?.url === "/interviewSummary/interview-summary-elastic") {
        if (this.applicationService.navBarSelectValue?.idItem.includes('CEC') ?? false) {
          this.populateTableWithQuestionsCec(res, tableConfig);
        } else if (this.applicationService.navBarSelectValue.idItem === "ONLINEREP") {
          let tableConfigOnlineRep = tableConfig;
          tableConfigOnlineRep.cols = tableConfigOnlineRep.cols.filter(el => el.columnDef !== "customerId")
            .filter(el => el.columnDef !== "reviewId").filter(el => el.columnDef !== "businessId").filter(el => el.columnDef !== "reviewEdited");
          //console.log("table:", tableConfigOnlineRep.cols)
          this.populateTableWithQuestionsElastic(res, tableConfigOnlineRep);
        } else {
          //console.log("old tableConfig Columns: ", tableConfig)
          this.populateTableWithQuestionsElastic(res, tableConfig);

          //this.populateTableWithQuestions(res, tableConfig);
        }
        //}
      }),
      tap((res) => this._interviews.next(res.items)),
      tap((_) => (this.isLoading = false))
    );
  }

  changeStatus(el: InterviewSummary, status: "approve" | "reject") {
    return of(null);
  }

  showDialogCommentsOnlineRep(el: any) {
    return this.dialog
      .open(OnlineRepCommentDialogComponent, {
        maxHeight: "120vh",
        maxWidth: "100vh",
        data: {
          reviewId: el.reviewId,
          dealerId: el.dealerId,
          marketId: el.marketId,
          regionId: el.regionId,
          brandId: el.brandId,
          section: el.section,
          comments: el.comments,
          response: el.response,
          customerName: el.customerName
        }
      })
      .afterClosed()
      .pipe(switchMap((formData) => (formData ? this.createCommentOnlineRep(formData) : of(null))))
  }

  private createCommentOnlineRep(formValue: any) {
    return this.http.post(this.endpoint + `v1/reviewReply`, formValue);
  }

  showDialogTagOnlineRep(el: any) {
    //console.log(el)
    return this.dialog
      .open(TagOnlineRepDialogComponent, {
        height: "auto",
        width: "450px",
        data: {
          businessId: el.businessId,
          reviewId: el.reviewId,
          section: el.section,
        }
      })
      .afterClosed()
      .pipe(switchMap((formData) => (formData ? this.createTagOnlineRep(formData) : of(null))))

  }

  private createTagOnlineRep(formValue: any): Observable<any> {
    this.loader.startLoader()
    return this.http.post(this.endpoint + `v1/setOnlineReputationReviewTag`, formValue).pipe(
      map((res: any) => res),
      finalize(() => this.loader.stopLoader()))

  }

  get(filter: Payload) {
    return this.http
      .post<any>(
        this.endpoint + "v4/getInterviewsSummary",
        filter
      )
      .pipe(
        map((res) => res),
        catchError((err) => {
          return of({} as any);
        })
      );
  }

  createRecoveryPlan(el: InterviewSummary) {
    /* let date = el.dataSurvey ? moment(el.dataSurvey) : undefined;
    let parsedDate = date?.format("YYYY-MM"); */
    return this.recoveryPlanService.createPlan({
      brand: el.brand,
      completedDate: el.completedDate,
      isEdit: false,
      outletId: el.outlet || "",
      surveyId: el.surveyRequestId,
      title: el.title,
      description: el.description,
      dateEnd: el.dateEnd,
    }).pipe(
      switchMap((r) => {

        let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
        payload.question.section = this.applicationService.navBarSelectValue.idItem;
        payload.question.locale = this.applicationService.languageSelectValue;
        return this.get(payload);
      }),
      tap(),
      catchError((err) => {


        return of([]);
      })
    );
  }

  createDataMining(el: InterviewSummary) {
    /* let date = el.dataSurvey ? moment(el.dataSurvey) : undefined;
    let parsedDate = date?.format("YYYY-MM"); */
    return this.dataMiningService.createPlan({
      brand: el.brand,
      completedDate: el.completedDate,
      isEdit: false,
      outletId: el.outlet || "",
      surveyId: el.surveyRequestId,
      title: el.title,
      description: el.description,
      dateEnd: el.dateEnd,
      contactReasonL1: "",
      contactReasonL2: "",
      dissatisfacionReason1Level1: "",
      dissatisfacionReason1Level2: "",
    }).pipe(
      switchMap((r) => {

        let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
        payload.question.section = this.applicationService.navBarSelectValue.idItem;
        payload.question.locale = this.applicationService.languageSelectValue;
        return this.get(payload);
      }),
      tap(),
      catchError((err) => {


        return of([]);
      })
    );
  }

  downloadExcel(el: InterviewSummary, isElastic: boolean, extraFilters: any) {
    const v3Interview = 'v3/getInterviewsSummary';
    if (this.router?.url === "/interviewSummary/interview-summary-elastic") {
      //console.log(this.router?.url)
      this.interviewSummaryPath = "v4/getInterviewsSummary";
      this.exportInterviewSummaryPath = "v4/exportInterviewsSummary";
    }
    else if (this.applicationService.menuIteSelectValue === 'Interviews summary') {
      //console.log(this.applicationService.menuIteSelectValue)
      this.interviewSummaryPath = v3Interview;
      this.exportInterviewSummaryPath = "v3/exportInterviewsSummary";
    }
    const currentAsyncApi = this.interviewSummaryPath === v3Interview ? 'v6/exportInterviewsSummary' : 'v5/exportInterviewsSummary';
    const exportInterviewSumPath = this.isInterviewSync ? currentAsyncApi : this.exportInterviewSummaryPath;
    const payload = this.buildFilters(this.utils.settingsPayloadValue.payload, 0, 0, 'ASC', 'surveyRequestId', extraFilters);

    payload.question.section = this.applicationService.navBarSelectValue.idItem;
    payload.question.tab = "REPORT";
    payload.question.locale = this.applicationService.languageSelectValue;

    //payload.extraFilters = extraFilters;

    return this.http
      .post(this.endpoint + exportInterviewSumPath, payload, {
        responseType: "blob",
      })
      .pipe(
        tap((data) => {
          if (exportInterviewSumPath != currentAsyncApi) {
            const file = new Blob([data], { type: "text/csv;charset=utf-8" });
            const fileURL = URL.createObjectURL(file);
            const a = document.createElement("a");
            a.href = fileURL;
            a.download = "exportInterviewsSummary" + ".xlsx";
            a.target = "_blank";
            a.click();
          } else {
            this.ds.openSnackBar();
          }

        })
      );
  }

  print(el: InterviewSummary) {
    return of("print");
  }

  private populateTableWithQuestionsCec(res: IMappedApiCollection<InterviewSummary>, tableConfig: TableStructure<any>) {
    const interview = res?.items[0] ?? {};
    //pattern to catch only questions eg: "Q1 - ..."
    const questionPattern = /Q[0-9]{1,2} -/;
    const questionsColumns = Object.keys(interview)?.filter(key => questionPattern.test(key)) ?? [];
    questionsColumns.forEach(questionKey => {
      const index = tableConfig.cols.findIndex(col => col.columnDef == questionKey)
      if (index == -1) {
        const value = interview[questionKey as keyof InterviewSummary]
        const question = value.split("#");
        const questionType = question[0];
        let column: TextCol<any> | RatingCol<any>;
        if (questionType === "TenStarNPS" || questionType === "TenStar" || questionType === "TenStarAvg") {
          column = new RatingCol(questionKey);
        } else {
          column = new TextCol(questionKey);
        }
        column.removable = false;
        column.isQuestionCol = true;
        tableConfig.cols.splice(-4, 0, column);
      }
    });
  }

  private populateTableWithQuestions(
    res: IMappedApiCollection<InterviewSummary>,
    tableConfig: TableStructure<any>
  ) {
    let interview = res.items[0];
    const columnsToHide = ["dealer", "importer"]

    for (let key in interview) {
      let col = tableConfig.cols.find((col) => col.columnDef === key);
      let val = interview[key as keyof InterviewSummary];
      if (
        (val || key[0] === "Q" || key.includes('categoryLevel')) &&
        typeof val === "string" &&
        key !== "region" &&
        key !== "rpCreateBtn" &&
        key !== "completedDate"
      ) {
        if (!col && !columnsToHide.includes(key)) {
          let q = val.split("#");
          let questionType = q[0];
          /* let questionValue = q[1];
          let f = key as Exclude<keyof InterviewSummary, "disabled">; */
          if (questionType === "TenStarNPS" || questionType === "TenStar" || questionType === "TenStarAvg") {
            //interview[f] = questionValue;
            tableConfig.cols.splice(-2, 0, new RatingCol(key));
          } else if (this.applicationService.navBarSelectValue?.idItem.includes('CEC') ?? false) {

            if (key != "model") {
              tableConfig.cols.splice(-2, 0, new TextCol(key, false, undefined, "", true));
            }

          } else if (key != "vehicle" && key != "nsatScore" && key != "ha" && key != "verbatim") {
            tableConfig.cols.splice(-2, 0, new TextCol(key, false, undefined, "", true));

          } else if (key === "verbatim" && this.applicationService.navBarSelectValue?.idItem.includes('CEC')) {
            tableConfig.cols.splice(-2, 0, new TextCol(key, false, undefined, "", true));
          }

        } else if (key != "vehicle" && key != "nsatScore" && key != "ha" && key != "verbatim") {
          tableConfig.cols.splice(-2, 0, new TextCol(key, false, undefined, "", true));

        } else if (key === "verbatim" && this.applicationService.navBarSelectValue?.idItem.includes('CEC')) {
          tableConfig.cols.splice(-2, 0, new TextCol(key, false, undefined, "", true));
        }
        let newCol = tableConfig.cols[tableConfig.cols.length - 3];

        newCol.removable = false;
        newCol.isQuestionCol = true;
      }
    }

    res.items.forEach((el) => {
      tableConfig.cols
        .filter((col) => col.isQuestionCol)
        .forEach((col) => {
          let val = el[col.columnDef as keyof InterviewSummary];
          if (val && typeof val === "string") {
            let q = val.split("#");
            let questionType = q[0];
            let questionValue = q[1] ? q[1] : val;
            let f = col.columnDef as Exclude<keyof InterviewSummary, "disabled">;
            el[f] = questionValue;
          }
        });
    });
  }

  private populateTableWithQuestionsElastic(
    res: IMappedApiCollection<InterviewSummary>,
    tableConfig: TableStructure<any>
  ) {
    let interview: InterviewSummary = res.items[0];
    const columnsToHide = ["dealer", "importer", "mined"];
    if (this.applicationService.navBarSelectValue.idItem === "ONLINEREP") {
      columnsToHide.push("businessId", "reviewId", "customerId");
      tableConfig.cols.forEach((col) => {
        if (col.columnDef === "responseDate") {
          col.isQuestionCol = true;
        }
      });
    }
    //tableConfig.cols = getColumns(this.user,this.question,this);
    for (let key in interview) {
      let col = tableConfig.cols.find((col) => col.columnDef === key);
      let val = interview[key as keyof InterviewSummary];
      if (
        (val || key[0] === "Q" || key.toLowerCase().startsWith("fu") || key.toLowerCase().startsWith("verbatim") || key.toLowerCase().startsWith("test")
          || key.toLowerCase().startsWith("td")
          || key.toLowerCase().startsWith("vp") || key.toLowerCase().startsWith("npr") || key.toLowerCase().startsWith("bm") || key.toLowerCase().startsWith("ob")
          || key[0] === "P"
        ) &&
        typeof val === "string" &&
        key !== "region" &&
        key !== "rpCreateBtn" &&
        key !== "completedDate"
      ) {
        if (!col && !columnsToHide.includes(key)) {
          let q = val.split("#");
          let questionType = q[0];
          /* let questionValue = q[1];
          let f = key as Exclude<keyof InterviewSummary, "disabled">; */
          if (questionType === "TenStarNPS" || questionType === "TenStar" || questionType === "TenStarAvg") {
            //interview[f] = questionValue;
            tableConfig.cols.splice(-2, 0, new RatingCol(key, undefined, true));
          } else if (this.applicationService.navBarSelectValue?.idItem.includes('CEC') ?? false) {

            if (key != "model") {
              tableConfig.cols.splice(-2, 0, new TextCol(key, false, undefined, "", true, undefined, undefined, true));
            }

          } else if (key != "vehicle" && key != "nsatScore" && key != "ha" && key != "verbatim" && key != "sentiment") {
            tableConfig.cols.splice(-2, 0, new TextCol(key, false, undefined, "", true, undefined, undefined, true));

          }

          let newCol = tableConfig.cols[tableConfig.cols.length - 3];

          newCol.removable = false;
          newCol.isQuestionCol = true;
        }
      }
    }

    res.items.forEach((el) => {
      tableConfig.cols
        .filter((col) => col.isQuestionCol)
        .forEach((col) => {
          let val = el[col.columnDef as keyof InterviewSummary];
          if (val && typeof val === "string") {
            if (col.columnDef === "responseDate") {
              val = val.split(" ")[0] + " " + val.split(" ")[1] + " " + val.split(" ")[2];
              //val = (new Date(val).toLocaleDateString()).toString();
              /*let value = new Date(val).toDateString();
              let noTime =  value.getMonth() + " " + value.getDay() + " " + value.getFullYear() ;
               val = noTime.toString();*/
              //console.log("data", val)
            }
            let q = val.split("#");
            let questionType = q[0];
            let questionValue = q[1] ? q[1] : val;
            let f = col.columnDef as Exclude<keyof InterviewSummary, "disabled">;
            el[f] = questionValue;
          }
        });
    });
  }

  private buildFilters(
    filter: any,
    skip: number,
    limit?: number,
    sortDirection?: string,
    sortBy?: string,
    extraFilters?: {
      NPS: string;
      NSAT: string;
      recovery: string;
      channel: string;
      searchBy: string;
      searchFor: string;
    }
  ) {
    let payload: IPayload = Object.assign({}, filter || {});
    let isNextPage = skip > this.lastPageIndexQuery;

    payload.question = {
      limit: "",
      locale: this.applicationService.languageSelectValue,
      orderByScore: "",
      questionCode: "",
      section: this.applicationService.navBarSelectValue.idItem,
      tab: this.applicationService.navBarSelectValue.idItem,
    };
    payload.extraFilters = {
      OrderByColumn: sortBy || "",
      Page: skip ? skip + 1 : 1,
      ItemsPerPage: limit,
      OrderBy: sortDirection || "",
      SearchColumn: extraFilters && extraFilters.searchBy ? extraFilters.searchBy : "",
      SearchFor: extraFilters && extraFilters.searchFor ? extraFilters.searchFor : "",
      Nps: extraFilters?.NPS || extraFilters?.NSAT || "",
      RpCreate: extraFilters?.recovery && extraFilters?.recovery === "Recovery" ? 1 : 0 || "",
      Channel: extraFilters?.channel || "",
      lastSort: isNextPage ? this.lastSort : null
    };
    if (sortBy === "surveyRequestId") {
      if (this.router?.url === "/interviewSummary/interview-summary-elastic") {
        payload.extraFilters.OrderByColumn = "surveyRequestId"
      }
      else if (this.applicationService.menuIteSelectValue === 'Interviews summary') {
        payload.extraFilters.OrderByColumn = "SurveyRequestID"
      }
    }


    // if (payload.extraFilters.OrderByColumn === "surveyRequestId") {
    //   payload.extraFilters.OrderByColumn = "SurveyRequestID";
    // }
    // if (payload.extraFilters.SearchColumn === "surveyRequestId") {
    //   payload.extraFilters.SearchColumn = "SurveyRequestID";
    // }
    return payload;
  }

  genTableConfig(user: MeUser, question: IQuestion) {
    this.user = user;
    this.question = question;
    const isSolOrDelivery = this.applicationService.navBarSelectValue.idItem === 'ORDER' || this.applicationService.navBarSelectValue.idItem === 'DELIVERY';
    const withDateSort = !this.router.url.endsWith('/interview-summary');
    let tableConfig = genInterviewSummaryTableConfig(this, user, question, isSolOrDelivery, this.ds, this.utils.settingsPayloadValue.payload, withDateSort, this.applicationService.navBarSelectValue.idItem);

    return tableConfig;
  }

  elementWasVisited(element: InterviewSummary) {
    let wasVisited = of(false);
    if (this.idTableValue) {
      wasVisited = this.idsTable.pipe(map((ids) => ids.includes(element.surveyRequestId)));
    }
    return wasVisited;
  }

  getResponsesTemplate() {
    return this.http.get(this.endpoint + `v1/getOnlineRepDealer`);
  }

  saveResponse(formData: any) {
    return this.http.post(this.endpoint + `v1/createOnlineRepResponse`, formData);
  }

  deleteResponse(idTemplate: string) {
    return this.http.get(this.endpoint + `v1/deleteOnlineRepDealerResponse?idTemplate=${idTemplate}`);
  }
}
